// ProductTour.tsx — the guided tour for new users (free and paid variants).
//
// Content lives in lib/tour/tours.ts, placement maths in lib/tour/geometry.ts
// and "has this user seen it" in lib/tour/progress.ts. This file is only the
// engine: it finds each step's `data-tour` anchor, spotlights it, positions
// the card, and moves the dashboard between views when a step needs one.
//
// Replaces OnboardingTooltips (5 class-selector steps, one shared tour for
// every plan). Anchors are data-tour attributes so a CSS rename can't orphan a
// step; tours.test.ts fails if one disappears from the source.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { capture } from "../lib/analytics";
import type { UserPlan } from "../lib/planCapabilities";
import { placeTip } from "../lib/tour/geometry";
import type { Box } from "../lib/tour/geometry";
import { stepsForAutoStart, stepsForReplay, writeProgress } from "../lib/tour/progress";
import { tourForPlan } from "../lib/tour/tours";
import type { TourStep, TourView } from "../lib/tour/types";

export const AUTO_START_DELAY_MS = 1200;
/** How long to wait for a step's anchor before skipping the step. */
export const ANCHOR_TIMEOUT_MS = 1800;
/** Approximate height of the mobile bottom sheet, kept clear of the target. */
const SHEET_RESERVE_PX = 220;

interface Props {
  userId: string;
  plan: UserPlan;
  /** False while something else (county picker, questionnaire) owns the screen. */
  enabled: boolean;
  currentView: TourView;
  onNavigate: (view: TourView) => void;
  onUpgrade?: () => void;
  /** Increment to replay the tour from the start (user-menu "Take the tour"). */
  replayNonce?: number;
}

function findAnchor(name: string): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`);
  for (const el of Array.from(nodes)) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

const toBox = (r: DOMRect): Box => ({ top: r.top, left: r.left, width: r.width, height: r.height });
const sameBox = (a: Box | null, b: Box | null) =>
  a === b || (!!a && !!b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);

const ProductTour: React.FC<Props> = ({ userId, plan, enabled, currentView, onNavigate, onUpgrade, replayNonce = 0 }) => {
  const def = tourForPlan(plan);
  const [steps, setSteps] = useState<TourStep[] | null>(null);
  const [index, setIndex] = useState(0);
  const [resolved, setResolved] = useState(false);
  const [target, setTarget] = useState<Box | null>(null);
  const [tip, setTip] = useState({ width: 0, height: 0 });
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });

  const cardRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const startView = useRef<TourView>(currentView);
  const returnFocus = useRef<Element | null>(null);
  const autoStartedFor = useRef<string | null>(null);
  const handledReplay = useRef(0);

  const step = steps ? steps[index] : null;
  const total = steps ? steps.length : 0;
  const ctx = useMemo(() => ({ plan }), [plan]);

  const begin = useCallback(
    (list: TourStep[]) => {
      if (list.length === 0) return;
      startView.current = currentView;
      returnFocus.current = document.activeElement;
      setIndex(0);
      setResolved(false);
      setTarget(null);
      setSteps(list);
    },
    [currentView]
  );

  // Automatic start: once per user+tour per page load, after the UI settles.
  useEffect(() => {
    if (!enabled || steps) return;
    const key = `${userId}:${def.id}`;
    if (autoStartedFor.current === key) return;
    const t = setTimeout(() => {
      autoStartedFor.current = key;
      begin(stepsForAutoStart(def, userId));
    }, AUTO_START_DELAY_MS);
    return () => clearTimeout(t);
  }, [enabled, steps, userId, def, begin]);

  // Replay from the user menu.
  useEffect(() => {
    if (replayNonce > 0 && replayNonce !== handledReplay.current) {
      handledReplay.current = replayNonce;
      capture("onboarding_tour_replayed", { tour: def.id });
      begin(stepsForReplay(def));
    }
  }, [replayNonce, def, begin]);

  const finish = useCallback(
    (outcome: "completed" | "dismissed") => {
      if (!steps) return;
      capture(outcome === "completed" ? "onboarding_tour_completed" : "onboarding_tour_dismissed", {
        tour: def.id,
        at_step: index + 1,
        of: steps.length,
        step_id: steps[index]?.id,
      });
      writeProgress(userId, def, outcome);
      setSteps(null);
      if (startView.current !== currentView) onNavigate(startView.current);
      const el = returnFocus.current;
      if (el instanceof HTMLElement) el.focus();
    },
    [steps, index, def, userId, currentView, onNavigate]
  );

  const advance = useCallback(() => {
    if (!steps) return;
    if (index >= steps.length - 1) finish("completed");
    else {
      setResolved(false);
      setIndex(index + 1);
    }
  }, [steps, index, finish]);

  const back = useCallback(() => {
    if (index === 0) return;
    setResolved(false);
    setIndex(index - 1);
  }, [index]);

  // Enter a step: switch view if needed, then wait for its anchor to exist.
  useEffect(() => {
    if (!step) return;
    capture("onboarding_tour_step_viewed", { tour: def.id, step: index + 1, of: total, step_id: step.id });
    if (step.view && step.view !== currentView) onNavigate(step.view);
    if (!step.anchor) {
      setTarget(null);
      setResolved(true);
      return;
    }
    const anchor = step.anchor;
    const started = Date.now();
    const poll = setInterval(() => {
      const el = findAnchor(anchor);
      if (el) {
        clearInterval(poll);
        const r = el.getBoundingClientRect();
        // On a phone the card is a bottom sheet; keep the target above it.
        const vh = window.innerHeight - (window.innerWidth <= 640 ? SHEET_RESERVE_PX : 0);
        if (r.top < 8 || r.bottom > vh - 8) el.scrollIntoView({ block: "center", inline: "nearest" });
        setResolved(true);
      } else if (Date.now() - started > ANCHOR_TIMEOUT_MS) {
        clearInterval(poll);
        // A step whose anchor never appears (feature hidden for this account,
        // class of bug that orphaned the old tour) is skipped, never frozen.
        capture("onboarding_tour_step_skipped", { tour: def.id, step_id: step.id });
        advance();
      }
    }, 100);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.id, steps]);

  // Track the anchor every frame while a step is showing: resize, layout
  // shifts and our own scrollIntoView all move it, and the spotlight must
  // never lag the element by even a pixel.
  useEffect(() => {
    if (!step || !resolved) return;
    let raf = 0;
    const tick = () => {
      const el = step.anchor ? findAnchor(step.anchor) : null;
      const next = el ? toBox(el.getBoundingClientRect()) : null;
      setTarget((prev) => (sameBox(prev, next) ? prev : next));
      setViewport((prev) =>
        prev.width === window.innerWidth && prev.height === window.innerHeight ? prev : { width: window.innerWidth, height: window.innerHeight }
      );
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [step, resolved]);

  // Measure the card so placement is computed from its real size.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    setTip((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
  }, [step, resolved, plan, viewport.width]);

  useEffect(() => {
    if (step && resolved) nextRef.current?.focus();
  }, [step, resolved]);

  useEffect(() => {
    if (!step) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        finish("dismissed");
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        advance();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        back();
      } else if (e.key === "Tab" && cardRef.current) {
        const f = Array.from(cardRef.current.querySelectorAll<HTMLElement>("button:not([disabled])"));
        if (f.length === 0) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [step, finish, advance, back]);

  const placement = useMemo(
    () => (step && resolved && tip.width > 0 ? placeTip({ target, tip, viewport, preferred: step.side }) : null),
    [step, resolved, target, tip, viewport]
  );

  if (!step || !steps) return null;

  const body = typeof step.body === "function" ? step.body(ctx) : step.body;
  const isLast = index === steps.length - 1;
  const showPlans = isLast && def.id === "free" && !!onUpgrade;

  let cardStyle: React.CSSProperties = { visibility: "hidden" };
  let side: string = "center";
  let arrowOffset = 0;
  let showArrow = false;
  let spot: Box | null = null;
  if (placement) {
    cardStyle = {};
    if (placement.mode === "center") {
      side = "center";
      cardStyle = { top: placement.top, left: placement.left };
    } else if (placement.mode === "sheet") {
      side = "sheet";
      spot = placement.spot;
    } else {
      side = placement.side;
      cardStyle = { top: placement.top, left: placement.left };
      arrowOffset = placement.arrow;
      showArrow = placement.showArrow;
      spot = placement.spot;
    }
  }

  return (
    <div className="ptour" data-tour-id={def.id}>
      <div className="ptour__shield" aria-hidden="true" />
      {spot ? (
        <div className="ptour__spot" style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }} aria-hidden="true" />
      ) : (
        <div className="ptour__dim" aria-hidden="true" />
      )}
      <div
        key={step.id}
        ref={cardRef}
        className={`ptour__card ptour__card--${side}`}
        style={cardStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ptour-title"
        aria-describedby="ptour-body"
      >
        {showArrow && <span className={`ptour__arrow ptour__arrow--${side}`} style={{ ["--arrow" as string]: `${arrowOffset}px` }} aria-hidden="true" />}
        <div className="ptour__progress" aria-hidden="true">
          <span className="ptour__count">
            {index + 1} / {total}
          </span>
          <span className="ptour__track">
            <span className="ptour__fill" style={{ width: `${((index + 1) / total) * 100}%` }} />
          </span>
        </div>
        <h2 id="ptour-title" className="ptour__title">
          {step.title}
        </h2>
        <p id="ptour-body" className="ptour__body">
          {body}
        </p>
        <div className="ptour__actions">
          <button type="button" className="ptour__skip" onClick={() => finish("dismissed")}>
            {isLast ? "Close" : "Skip tour"}
          </button>
          <span className="ptour__spacer" />
          {index > 0 && (
            <button type="button" className="ptour__btn ptour__btn--ghost" onClick={back}>
              Back
            </button>
          )}
          {showPlans && (
            <button
              type="button"
              className="ptour__btn ptour__btn--ghost"
              onClick={() => {
                finish("completed");
                onUpgrade?.();
              }}
            >
              See plans
            </button>
          )}
          <button ref={nextRef} type="button" className="ptour__btn ptour__btn--primary" onClick={advance}>
            {isLast ? def.doneLabel || "Done" : index === 0 ? "Start tour" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductTour;
